import { Box, Button, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useRef, useState } from "react";
import ExportButton from "components/button/ExportButton";
import Notify from "config/notify";
import routes from "constants/routes";
import { SESSION_KEY } from "constants/key";
import CarTiresizeForm, { CarTiresizeFormRef } from "../components/CarTiresizeForm";
import useTiresize from "../hooks/useTiresize";

/**
 * Search component renders the search page with a header and export button.
 */
function Search() {
  const [isCustom, setIsCustom] = useState(false);
  const navigate = useNavigate();
  const tireSizeFormFrontRef = useRef<CarTiresizeFormRef>(null);
  const tireSizeFormRearRef = useRef<CarTiresizeFormRef>(null);

  const {
    widthList,
    aspectRatioFrontList,
    rimDiamFrontList,
    frontTire,
    setFrontTire,
    aspectRatioRearList,
    rimDiamRearList,
    rearTire,
    setRearTire,
    handleSubmit,
  } = useTiresize();

  const onSubmit = () => {
    if (
      (tireSizeFormFrontRef.current && tireSizeFormFrontRef.current.validate()) ||
      (isCustom && tireSizeFormRearRef.current && tireSizeFormRearRef.current.validate())
    ) {
      Notify.setNotification({
        show: true,
        title: "必須項目が入力されていません",
        actionsPosition: "end",
      });
      return;
    }

    const tires = [];

    if (
      frontTire &&
      frontTire.tire_width &&
      frontTire.tire_aspect_ratio &&
      frontTire.tire_rim_diam
    ) {
      tires.push({
        tire_width: frontTire.tire_width,
        tire_aspect_ratio: frontTire.tire_aspect_ratio,
        tire_rim_diam: frontTire.tire_rim_diam,
        tire_loin_speed_mark: frontTire.load_index,
      });
    }

    if (rearTire && rearTire.tire_width && rearTire.tire_aspect_ratio && rearTire.tire_rim_diam) {
      tires.push({
        tire_width: rearTire.tire_width,
        tire_aspect_ratio: rearTire.tire_aspect_ratio,
        tire_rim_diam: rearTire.tire_rim_diam,
        tire_loin_speed_mark: rearTire.load_index,
      });
    }

    sessionStorage.setItem(SESSION_KEY.NEW_QUOTE_FROM, "tiresize");
    sessionStorage.setItem(SESSION_KEY.NEW_QUOTE_TIRE, JSON.stringify(tires));
    const tireSizeFormatter = (tire: typeof frontTire, prefix: string) =>
      `${prefix} ${tire?.tire_width}/${tire?.tire_aspect_ratio}R${tire?.tire_rim_diam}`;

    let selectedTire;
    if (frontTire?.tire_width && !rearTire?.tire_width) {
      selectedTire = {
        mmm_car_tiresize: tireSizeFormatter(frontTire, ""),
      };
    } else if (!frontTire?.tire_width && rearTire?.tire_width) {
      selectedTire = {
        mmm_car_tiresize: tireSizeFormatter(rearTire, ""),
      };
    } else {
      selectedTire = {
        mmm_car_tiresize: [
          frontTire?.tire_width ? tireSizeFormatter(frontTire, "F") : null,
          rearTire?.tire_width ? tireSizeFormatter(rearTire, "R") : null,
        ]
          .filter(Boolean)
          .join("    "),
      };
    }

    sessionStorage.setItem(SESSION_KEY.NEW_QUOTE_MAKER, JSON.stringify({ selectedTire }));

    handleSubmit();
  };

  const handleCustomChange = (newIsCustom: boolean) => {
    setIsCustom(false);
  };

  return (
    <>
      <Box sx={{ px: { xs: 2, md: 2.5, lg: 5 }, pt: { xs: 2, md: 2.5, lg: 3 } }}>
        <Button onClick={() => navigate(-1)} variant="custom-outlined">
          <Box>＜ 戻る</Box>
        </Button>
      </Box>
      <Box sx={{ p: { xs: 2, md: 2.5, lg: 5 }, display: { sm: "block", lg: "flex" } }}>
      <Box marginBottom={3}>
        <Typography
          sx={{
            fontWeight: "medium",
            color: "var(--gray-700)",
            fontSize: "25px",
            marginBottom: "22px",
          }}
        >
          新規見積
        </Typography>
        <ExportButton label="見積一覧" onClick={() => navigate(routes.quoteList)} />
      </Box>
      <Box
        sx={{
          marginX: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        <CarTiresizeForm
          ref={tireSizeFormFrontRef}
          label="タイヤサイズから選ぶ"
          onCustomChange={handleCustomChange}
          widthList={widthList}
          aspectRatioList={aspectRatioFrontList}
          rimDiamList={rimDiamFrontList}
          selectedTire={frontTire}
          setSelectedTire={setFrontTire}
          onSubmit={onSubmit}
          isCustom={isCustom}
        />

        {isCustom && (
          <>
            <Box
              sx={{
                width: "100%",
                borderTop: "4px solid var(--silver-150)",
              }}
            />
            <CarTiresizeForm
              ref={tireSizeFormRearRef}
              label="リアタイヤサイズを選択"
              onSubmit={onSubmit}
              widthList={widthList}
              aspectRatioList={aspectRatioRearList}
              rimDiamList={rimDiamRearList}
              selectedTire={rearTire}
              setSelectedTire={setRearTire}
              isDisplayNote
              sx={{
                py: 4,
                marginX: "auto",
              }}
            />
          </>
        )}
      </Box>
    </Box>
    </>
  );
}

export default Search;
